import React, { useMemo } from 'react';
import { SingleRecording } from '../../../../queries/ats/recordings/types/recording';
import { EpisodeAsset } from '../../../../queries/ats/titles/types/asset';
import { Source } from '../../../../queries/ats/titles/types/sources';
import { getPreferredRecording } from '../../../../queries/ats/titles/utils/recording-utils';
import { details } from '../../../../routes/routes';
import { classes } from '../../../../utils/commonUtils';
import { useDetails } from '../../details-context';
import { DetailsLink } from '../../details-link';
import { useInputContext } from '../../use-input-context';
import { EpisodeBadge } from './episode-badge';
import { EpisodeInfoLine } from './episode-info-line';
import { EpisodeThumbnail } from './episode-thumbnail';
import css from './episode.module.scss';

interface EpisodeProps {
  episode: EpisodeAsset;
}

export function Episode({ episode }: EpisodeProps) {
  const { selectedContext } = useDetails('DetailsSeriesView');
  const inputContext = useInputContext();
  const epSequence = episode.episode.sequenceNumber.toString();

  const source = useMemo(() => {
    return selectedContext.preferredSource(episode, inputContext);
  }, [selectedContext, episode, inputContext]);

  const isRecording = details.isRecording(episode);
  const episodeNumber = epSequence.length > 3 ? epSequence.split('').slice(0, 3).concat() : epSequence;

  const recording = source.type === 'program' ? getPreferredRecording(source, true) : undefined;

  if (recording !== undefined) {
    return <EpisodeRecording episode={episode} source={source} recording={recording} />;
  }

  return (
    <DetailsLink
      className={css.link}
      id={episode.masterId}
      type={isRecording ? 'recording' : episode.type}
      subType={isRecording ? 'single' : undefined}
      params={{
        orderByProviderId: source.id,
        platformTitleId: source.platformTitleId,
      }}
    >
      <article className={css.episode}>
        <span className={css.number}>{episodeNumber}</span>

        <EpisodeThumbnail episode={episode} source={source} />

        <div className={css.content}>
          <div className={css.title}>{episode.titleName}</div>
          <EpisodeInfoLine episode={episode} source={source} />
          <p className={css.description}>{episode.descriptionLong || episode.descriptionShort}</p>
        </div>

        <EpisodeBadge source={source} />
      </article>
    </DetailsLink>
  );
}

interface EpisodeRecordingProps {
  episode: EpisodeAsset;
  source: Source;
  recording: SingleRecording;
}

function EpisodeRecording({ episode, source, recording }: EpisodeRecordingProps) {
  const epSequence = episode.episode.sequenceNumber.toString();
  const episodeNumber = epSequence.length > 3 ? epSequence.split('').slice(0, 3).concat() : epSequence;

  return (
    <DetailsLink
      className={css.link}
      id={recording.programId}
      type="recording"
      subType="single"
      params={{
        orderByProviderId: recording.channelId,
        platformTitleId: recording.programId,
      }}
    >
      <article className={css.episode}>
        <span className={css.number}>{episodeNumber}</span>

        <EpisodeThumbnail episode={episode} source={source} />

        <div className={css.content}>
          <div className={css.title}>{episode.titleName}</div>
          <EpisodeInfoLine episode={episode} source={source} />
          <p className={css.description}>{episode.descriptionLong || episode.descriptionShort}</p>
        </div>

        <EpisodeBadge source={source} />
      </article>
    </DetailsLink>
  );
}

export function EpisodeSkeleton() {
  return (
    <div className={classes(css.episode, css.skeleton)}>
      <div className={css.number} />
      <div className={css.image} />
      <div className={css.content}>
        <div className={css.title} />
        <div className={css.info} />
        <div className={css.description} />
        <div className={css.description} />
      </div>
      <div className={css.logo} />
    </div>
  );
}
